import React, {memo, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Button, Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import {toast} from "react-toastify";
import PropTypes from "prop-types";
import {
  requestAvailablePins,
  requestProbesTypesList,
  requestSensorList,
  requestSensorTypeList,
  requestSensorGroupList,
} from "../../redux/Actions/controllers.actions";
import {getSelectedController} from "../../redux/Selectors/controllers.selectors";
import SelectCreator from "../Forms/components/SelectCreator";
import Loader from "../Loader";

const fields = {
  input: {
    first: {label: "Probe", placeholder: "Select probe"},
    second: {label: "Sensor", placeholder: "Select sensor"},
    third: {label: "Type", placeholder: "Select Type"},
    fourth: {label: "Pin", placeholder: "Select pin number"},
    fifth: {label: "Group", placeholder: "Select Group"},
  },
  output: {
    first: {label: "Output Type", placeholder: "Select output type"},
    second: {label: "Feed Type", placeholder: "Select feed type"},
    third: {label: "Type", placeholder: "Select Type"},
    fourth: {label: "Pin", placeholder: "Select pin number"},
    fifth: {label: "Group", placeholder: "Select Group"},
  },
};

const useHandleForm = (ioType, onSend) => {
  const dispatch = useDispatch();

  const InitialState = {
    IO_TYPE_PROBE: ioType,
  };
  const [payload, setPayload] = useState(InitialState);
  const {IO_TYPE_PROBE, probe, sensor, pin, type_id} = payload;
  const selectedController = useSelector(getSelectedController);
  const category = selectedController.category;
  const [probesList, setProbesList] = useState([]);
  const [sensorList, setSensorList] = useState([]);
  const [pinsList, setPinsList] = useState([]);
  const [typeList, setTypeList] = useState([]);
  const [groupList, setGroupList] = useState([]);

  useEffect(() => {
    dispatch(requestProbesTypesList(IO_TYPE_PROBE, setProbesList));
    dispatch(requestSensorTypeList(category, setTypeList));
    dispatch(requestSensorGroupList(setGroupList));
  }, [IO_TYPE_PROBE, category, dispatch]);

  const handleProbeChange = (e) => {
    const {value} = e.target;
    const obj = {IO_TYPE_PROBE, probe: value};
    setPayload(obj);
    dispatch(requestSensorList({IO_TYPE_PROBE, probe: value}, setSensorList));
  };

  const handleSensorChange = (e) => {
    const {value} = e.target;
    const obj = {IO_TYPE_PROBE, probe, sensor: value};
    setPayload(obj);

    const callback = (data) => {
      setPinsList(data.map((eachPin) => ({label: eachPin, value: eachPin})));
    };

    dispatch(
      requestAvailablePins(
        {
          IO_TYPE_PROBE,
          probe,
          sensor: value,
        },
        callback
      )
    );
  };

  const handleFieldChange = (e) => {
    const {name, value} = e.target;
    setPayload({...payload, [name]: value});
  };

  const handleMenuOpen = () => {
    if (!category) {
      toast.error("Inventory doesn't have a valid category");
    }
  };

  const submitHandler = () => {
    const {probe, sensor, pin, type_id, group_id} = payload;
    if (!probe || !sensor || !pin || !type_id || !group_id) {
      toast.error("Please select required fields");
    } else {
      onSend({...payload});
    }
  };

  return {
    handleProbeChange,
    handleSensorChange,
    handleFieldChange,
    handleMenuOpen,
    payload,
    probesList,
    sensorList,
    pinsList: type_id ? pinsList : [],
    typeList: sensor ? typeList : [],
    groupList: pin ? groupList : [],
    submitHandler,
  };
};

const ProbesModal = (props) => {
  const {open, onSend, onCancel, ioType} = props;
  const {
    handleProbeChange,
    handleSensorChange,
    handleFieldChange,
    handleMenuOpen,
    payload,
    probesList,
    sensorList,
    pinsList,
    typeList,
    groupList,
    submitHandler,
  } = useHandleForm(ioType, onSend);
  const {probe, sensor, pin, type_id, group_id} = payload;

  return (
    <Modal unmountOnClose={true} isOpen={open} backdrop={"static"}>
      <Loader />
      <ModalHeader>{`Add ${ioType}`}</ModalHeader>
      <ModalBody>
        <SelectCreator
          label={fields[ioType].first.label}
          placeholder={fields[ioType].first.placeholder}
          value={probe}
          name={"probe"}
          options={probesList}
          onChange={handleProbeChange}
          large
          isRequired
          classNamePrefix={"select_probe"}
          id={"select_probe"}
        />
        <SelectCreator
          label={fields[ioType].second.label}
          placeholder={fields[ioType].second.placeholder}
          value={sensor}
          name={"sensor"}
          options={sensorList}
          onChange={handleSensorChange}
          large
          isRequired
          isSearchable
          classNamePrefix={"select_sensor"}
          id={"select_sensor"}
        />
        <SelectCreator
          label={fields[ioType].third.label}
          placeholder={fields[ioType].third.placeholder}
          value={type_id}
          name={"type_id"}
          options={typeList}
          onChange={handleFieldChange}
          onMenuOpen={handleMenuOpen}
          large
          isRequired
          classNamePrefix={"select_type"}
          id={"select_type"}
        />
        <SelectCreator
          label={fields[ioType].fourth.label}
          placeholder={fields[ioType].fourth.placeholder}
          value={pin}
          name={"pin"}
          options={pinsList}
          onChange={handleFieldChange}
          large
          isRequired
          classNamePrefix={"select_pin"}
          id={"select_pin"}
        />
        <SelectCreator
          label={fields[ioType].fifth.label}
          placeholder={fields[ioType].fifth.placeholder}
          value={group_id}
          name={"group_id"}
          options={groupList}
          onChange={handleFieldChange}
          large
          isRequired
          classNamePrefix={"select_group"}
          id={"select_group"}
        />
      </ModalBody>
      <ModalFooter>
        <Button onClick={submitHandler} data-cy="submit-btn">
          SAVE
        </Button>
        <Button onClick={onCancel} data-cy="cancel-btn">
          CANCEL
        </Button>
      </ModalFooter>
    </Modal>
  );
};

function areEqual(prevProps, nextProps) {
  return prevProps.open === nextProps.open;
}

ProbesModal.propTypes = {
  open: PropTypes.bool,
  onSend: PropTypes.func,
  onCancel: PropTypes.func,
};
export default memo(ProbesModal, areEqual);
